// src/pages/SalesPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { FiTag, FiClock, FiShoppingBag, FiArrowRight, FiGift, FiZap, FiCheckCircle } from 'react-icons/fi';
import styles from './SalesPage.module.css';

const SalesPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [sale, setSale] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [progress, setProgress] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const sectionRef = useRef(null);
  const [user, setUser] = useState(null);
  const [addingToCart, setAddingToCart] = useState({}); // track per product

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get current user
  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    };
    getUser();
  }, []);

  // Fetch sale
  useEffect(() => {
    if (slug) fetchSale();
  }, [slug]);

  // Countdown timer
  useEffect(() => {
    if (sale) {
      calculateTimeLeft();
      calculateProgress();
      const timer = setInterval(calculateTimeLeft, 1000);
      return () => clearInterval(timer);
    }
  }, [sale]);

  const calculateTimeLeft = () => {
    if (!sale) return;
    const now = Date.now();
    const end = new Date(sale.end_date).getTime();
    const diff = end - now;
    if (diff <= 0) {
      setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      return;
    }
    setTimeLeft({
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((diff % (1000 * 60)) / 1000),
    });
  };

  const calculateProgress = () => {
    if (!sale) return;
    const now = Date.now();
    const start = new Date(sale.start_date).getTime();
    const end = new Date(sale.end_date).getTime();
    const total = end - start;
    const elapsed = now - start;
    if (total <= 0) { setProgress(0); return; }
    const pct = Math.min(100, Math.max(0, (elapsed / total) * 100));
    setProgress(pct);
  };

  const fetchSale = async () => {
    setLoading(true);
    setError('');
    try {
      const { data: saleData, error: saleError } = await supabase
        .from('sales')
        .select('*')
        .eq('slug', slug)
        .eq('is_active', true)
        .single();

      if (saleError || !saleData) {
        setError('Sale not found or inactive.');
        setLoading(false);
        return;
      }

      const now = new Date();
      const start = new Date(saleData.start_date);
      const end = new Date(saleData.end_date);
      if (now < start || now > end) {
        setError('This sale is not currently active.');
        setLoading(false);
        return;
      }

      setSale(saleData);

      const { data: saleProducts } = await supabase
        .from('sale_products')
        .select('product_id')
        .eq('sale_id', saleData.id);

      if (saleProducts && saleProducts.length > 0) {
        const productIds = saleProducts.map(sp => sp.product_id);
        const { data: productData } = await supabase
          .from('products')
          .select('*')
          .in('id', productIds)
          .order('name');
        setProducts(productData || []);
      } else {
        setProducts([]);
      }
    } catch (err) {
      console.error('Error fetching sale:', err);
      setError('Failed to load sale.');
    } finally {
      setLoading(false);
    }
  };

  const discountedPrice = (product) => {
    if (!sale) return product.price;
    if (sale.discount_type === 'percentage') {
      return product.price * (1 - sale.discount_value / 100);
    } else {
      return Math.max(0, product.price - sale.discount_value);
    }
  };

  const scrollToProducts = () => {
    sectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Add to Cart handler
  const handleAddToCart = async (product, e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      navigate('/login');
      return;
    }

    setAddingToCart(prev => ({ ...prev, [product.id]: true }));

    const size = 'Standard';
    const quantity = 1;

    try {
      const { data: existing } = await supabase
        .from('cart_items')
        .select('*')
        .eq('user_id', user.id)
        .eq('product_id', product.id)
        .eq('size', size)
        .single();

      if (existing) {
        await supabase
          .from('cart_items')
          .update({ quantity: existing.quantity + quantity })
          .eq('id', existing.id);
      } else {
        await supabase
          .from('cart_items')
          .insert({
            user_id: user.id,
            product_id: product.id,
            quantity: quantity,
            size: size
          });
      }

      // Dispatch event
      const event = new CustomEvent('itemAddedToCart', {
        detail: { productName: product.name, quantity }
      });
      window.dispatchEvent(event);

      // Button feedback
      const btn = e.currentTarget;
      const originalText = btn.innerHTML;
      btn.innerHTML = '✓ Added!';
      setTimeout(() => {
        btn.innerHTML = originalText;
        setAddingToCart(prev => ({ ...prev, [product.id]: false }));
      }, 1500);
    } catch (err) {
      console.error('Add to cart error:', err);
      setAddingToCart(prev => ({ ...prev, [product.id]: false }));
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading amazing deals...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorIcon}>🔍</div>
        <h2>Sale Not Found</h2>
        <p>{error}</p>
        <Link to="/" className={styles.backHome}>Return Home</Link>
      </div>
    );
  }

  if (!sale) return null;

  // ============================================
  // MOBILE UI – GRID LAYOUT (2 columns)
  // ============================================
  if (isMobile) {
    return (
      <div className={styles.mobileSalesPage}>
        {/* Hero – Compact */}
        <div className={styles.mobileHero} style={{ backgroundImage: `url(${sale.image_url || '/placeholder.jpg'})` }}>
          <div className={styles.mobileHeroOverlay}>
            <span className={styles.mobileSaleBadge}>
              <FiZap /> {sale.discount_type === 'percentage' ? `${sale.discount_value}% OFF` : `₹${sale.discount_value} OFF`}
            </span>
            <h1>{sale.name}</h1>
            <p>{sale.description || 'Hurry, limited time offer!'}</p>
          </div>
        </div>

        {/* Countdown + Progress – Mobile */}
        <div className={styles.mobileCountdownSection}>
          <div className={styles.countdownWrapper}>
            <div className={styles.countdownItem}>
              <span>{String(timeLeft.days).padStart(2, '0')}</span>
              <label>Days</label>
            </div>
            <span className={styles.countdownSeparator}>:</span>
            <div className={styles.countdownItem}>
              <span>{String(timeLeft.hours).padStart(2, '0')}</span>
              <label>Hrs</label>
            </div>
            <span className={styles.countdownSeparator}>:</span>
            <div className={styles.countdownItem}>
              <span>{String(timeLeft.minutes).padStart(2, '0')}</span>
              <label>Min</label>
            </div>
            <span className={styles.countdownSeparator}>:</span>
            <div className={styles.countdownItem}>
              <span>{String(timeLeft.seconds).padStart(2, '0')}</span>
              <label>Sec</label>
            </div>
          </div>
          <div className={styles.progressBar}>
            <div className={styles.progressFill} style={{ width: `${Math.min(100, progress)}%` }}></div>
          </div>
          <div className={styles.progressText}>
            <span>Sale in progress</span>
            <span>{Math.round(progress)}%</span>
          </div>
        </div>

        {/* Products – Grid (2 columns) */}
        <div className={styles.mobileSection}>
          <div className={styles.mobileSectionHeader}>
            <h2>🔥 Grab the Deals</h2>
            <span>{products.length} items</span>
          </div>
          <div className={styles.mobileProductsGrid}>
            {products.map(product => {
              const price = discountedPrice(product);
              const original = product.price;
              return (
                <div key={product.id} className={styles.mobileProductCardGrid}>
                  <Link to={`/product/${product.id}`} className={styles.mobileCardLink}>
                    <div className={styles.mobileCardImage}>
                      <img src={product.image_url || '/placeholder.jpg'} alt={product.name} />
                      <div className={styles.mobileDiscountBadge}>
                        {sale.discount_type === 'percentage' ? `${sale.discount_value}%` : `₹${sale.discount_value} OFF`}
                      </div>
                    </div>
                    <div className={styles.mobileCardContent}>
                      <h3>{product.name}</h3>
                      <div className={styles.mobilePrice}>
                        <span className={styles.mobileOriginalPrice}>₹{original}</span>
                        <span className={styles.mobileDealPrice}>₹{Math.round(price)}</span>
                      </div>
                    </div>
                  </Link>
                  <button
                    className={styles.mobileAddBtnGrid}
                    onClick={(e) => handleAddToCart(product, e)}
                    disabled={addingToCart[product.id]}
                  >
                    <FiShoppingBag /> {addingToCart[product.id] ? 'Adding...' : 'Add'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Floating CTA */}
        <div className={styles.mobileFloatingBtn}>
          <button onClick={scrollToProducts}>
            <FiShoppingBag /> Shop Now
          </button>
        </div>
      </div>
    );
  }

  // ============================================
  // DESKTOP UI (unchanged)
  // ============================================
  return (
    <div className={styles.salesPage}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroBg} style={{ backgroundImage: `url(${sale.image_url || '/placeholder.jpg'})` }}>
          <div className={styles.heroOverlay}>
            <div className={styles.heroContent}>
              <div className={styles.heroBadge}><FiZap /> Limited Time Offer</div>
              <h1>{sale.name}</h1>
              <p>{sale.description || "Don't miss out on these exclusive deals!"}</p>
              <div className={styles.heroMeta}>
                <span><FiTag /> {sale.discount_type === 'percentage' ? `${sale.discount_value}% OFF` : `₹${sale.discount_value} OFF`}</span>
                <span><FiClock /> {new Date(sale.start_date).toLocaleDateString()} - {new Date(sale.end_date).toLocaleDateString()}</span>
              </div>
              <div className={styles.heroCountdown}>
                <div className={styles.countdownItem}>
                  <span>{String(timeLeft.days).padStart(2, '0')}</span>
                  <label>Days</label>
                </div>
                <div className={styles.countdownItem}>
                  <span>{String(timeLeft.hours).padStart(2, '0')}</span>
                  <label>Hours</label>
                </div>
                <div className={styles.countdownItem}>
                  <span>{String(timeLeft.minutes).padStart(2, '0')}</span>
                  <label>Minutes</label>
                </div>
                <div className={styles.countdownItem}>
                  <span>{String(timeLeft.seconds).padStart(2, '0')}</span>
                  <label>Seconds</label>
                </div>
              </div>
              <button onClick={scrollToProducts} className={styles.heroCta}>
                Shop Now <FiArrowRight />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section – Grid */}
      <section ref={sectionRef} className={styles.productsSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <div>
              <h2>🔥 Featured Products</h2>
              <p className={styles.sectionSub}>Grab these amazing deals before they expire!</p>
            </div>
            <div className={styles.sectionStats}>
              <span>{products.length} items</span>
              <span>•</span>
              <span>Up to {sale.discount_type === 'percentage' ? `${sale.discount_value}% off` : `₹${sale.discount_value} off`}</span>
            </div>
          </div>

          {products.length === 0 ? (
            <div className={styles.empty}>
              <FiGift className={styles.emptyIcon} />
              <h3>No Products in This Sale</h3>
              <p>Check back later for exciting deals!</p>
            </div>
          ) : (
            <div className={styles.productsGrid}>
              {products.map(product => {
                const price = discountedPrice(product);
                const original = product.price;
                const discount = sale.discount_type === 'percentage'
                  ? sale.discount_value
                  : Math.round((sale.discount_value / original) * 100);

                return (
                  <div key={product.id} className={styles.productCard}>
                    <div className={styles.cardImage}>
                      <img src={product.image_url || '/placeholder.jpg'} alt={product.name} />
                      <div className={styles.discountBadge}>-{discount}%</div>
                      <div className={styles.cardOverlay}>
                        <Link to={`/product/${product.id}`} className={styles.viewBtn}>
                          View Product
                        </Link>
                      </div>
                    </div>
                    <div className={styles.cardContent}>
                      <h3>{product.name}</h3>
                      <div className={styles.priceSection}>
                        <span className={styles.originalPrice}>₹{original}</span>
                        <span className={styles.dealPrice}>₹{Math.round(price)}</span>
                      </div>
                      <div className={styles.cardFooter}>
                        <span className={styles.saleTag}><FiTag /> Sale</span>
                        <button
                          className={styles.addToCartBtn}
                          onClick={(e) => handleAddToCart(product, e)}
                          disabled={addingToCart[product.id]}
                        >
                          <FiShoppingBag /> {addingToCart[product.id] ? 'Adding...' : 'Add to Cart'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default SalesPage;